import { useCallback, useEffect, useRef, useState } from "react";
import { attachSpectrum, type SpectrumTap } from "./spectrum.js";
import {
  VISUALIZER_MODES,
  createRenderer,
  type Backend,
  type Frame,
  type Renderer,
  type VisualizerMode,
} from "./renderer.js";
import "./visualizer.css";

const MODE_STORAGE_KEY = "ees.visualizer.mode";

/** Selected mode, remembered across visits. */
export function useVisualizerMode(): [VisualizerMode, (mode: VisualizerMode) => void] {
  const [mode, setMode] = useState<VisualizerMode>(() => {
    const saved = window.localStorage.getItem(MODE_STORAGE_KEY);
    return VISUALIZER_MODES.find((entry) => entry === saved) ?? "synthwave";
  });

  useEffect(() => {
    window.localStorage.setItem(MODE_STORAGE_KEY, mode);
  }, [mode]);

  return [mode, setMode];
}

export interface VisualizerProps {
  /**
   * Supplies the media element to analyse. Called every frame so the tap
   * attaches as soon as playback starts without re-rendering the tree.
   */
  getMedia?: () => HTMLMediaElement | null;
  mode: VisualizerMode;
  className?: string;
  /** Called once with the backend that ended up rendering. */
  onBackend?: (backend: Backend) => void;
}

/**
 * Canvas that draws the selected mode at display refresh.
 *
 * Renders a gentle idle drift whenever there is nothing to analyse — station
 * offline, tab muted — so the panel never looks broken.
 */
export function Visualizer({ getMedia, mode, className, onBackend }: VisualizerProps): JSX.Element | null {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mediaRef = useRef(getMedia);
  const backendRef = useRef(onBackend);
  const rendererRef = useRef<Renderer | null>(null);
  const modeRef = useRef<VisualizerMode>(mode);
  const [failed, setFailed] = useState(false);

  mediaRef.current = getMedia;
  backendRef.current = onBackend;
  modeRef.current = mode;

  useEffect(() => {
    setFailed(false);
    const canvas = canvasRef.current;
    if (!canvas) return;

    let cancelled = false;
    let frameId = 0;
    let inView = true;
    let tap: SpectrumTap | null = null;
    const levels = new Float32Array(32);
    const startedAt = performance.now();

    const frame: Frame = {
      time: 0,
      bass: 0,
      mid: 0,
      treble: 0,
      level: 0,
      levels,
    };

    const resize = (): void => {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const width = Math.max(1, Math.round((canvas.clientWidth || 320) * dpr));
      const height = Math.max(1, Math.round((canvas.clientHeight || 180) * dpr));
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }
    };

    const observer =
      "IntersectionObserver" in window
        ? new IntersectionObserver((entries) => {
            inView = entries.some((entry) => entry.isIntersecting);
          })
        : null;
    observer?.observe(canvas);

    // The backing store must track the element even while it is off-screen:
    // the first frame after a scroll would otherwise stretch a stale bitmap.
    const resizeObserver =
      "ResizeObserver" in window
        ? new ResizeObserver(() => {
            resize();
          })
        : null;
    resizeObserver?.observe(canvas);
    window.addEventListener("resize", resize);
    resize();

    void createRenderer(canvas, modeRef.current)
      .then((renderer) => {
        if (cancelled) {
          renderer.destroy();
          return;
        }
        rendererRef.current = renderer;
        backendRef.current?.(renderer.backend);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.warn("[visualizer] no renderer available:", error);
        setFailed(true);
      });

    let frames = 0;

    const tick = (now: number): void => {
      frameId = requestAnimationFrame(tick);
      const renderer = rendererRef.current;
      if (!renderer || !inView || document.hidden) return;

      renderer.setMode(modeRef.current);
      frame.time = (now - startedAt) / 1000;

      const media = mediaRef.current?.() ?? null;
      const analysing = media !== null && !media.muted && media.volume > 0 && !media.paused && !media.ended;
      if (analysing && media) {
        tap = attachSpectrum(media);
      } else {
        tap = null;
      }

      if (tap?.update()) {
        frame.bass = tap.bass;
        frame.mid = tap.mid;
        frame.treble = tap.treble;
        frame.level = tap.level;
        levels.set(tap.levels);
      } else {
        drift(frame.time, levels);
        frame.bass = average(levels, 0, 6);
        frame.mid = average(levels, 6, 20);
        frame.treble = average(levels, 20, 32);
        frame.level = average(levels, 0, 32);
      }

      // Cheap observable state for support/debugging: which signal is driving
      // the picture, and how strong it is. `heard` only flips once the analyser
      // has actually seen non-silent bins, so `live` proves real audio data.
      frames += 1;
      if (frames % 30 === 0) {
        const source = !tap ? "idle" : tap.heard ? "live" : "attached";
        const ctx = tap ? tap.analyser.context.state : "none";
        const raw = tap ? tap.rawPeak : -1;
        canvas.dataset.spectrum = `${source}:${frame.level.toFixed(2)}:ctx=${ctx}:raw=${raw}`;
      }

      try {
        renderer.render(frame);
      } catch (error) {
        console.warn("[visualizer] render failed, falling back:", error);
        renderer.destroy();
        rendererRef.current = null;
        setFailed(true);
      }
    };

    frameId = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
      observer?.disconnect();
      resizeObserver?.disconnect();
      window.removeEventListener("resize", resize);
      rendererRef.current?.destroy();
      rendererRef.current = null;
      tap = null;
    };
    // The renderer is bound to the canvas element; mode changes go through
    // `modeRef`, so this effect only runs on mount.
  }, []);

  if (failed) return null;

  return <canvas ref={canvasRef} className={`viz ${className ?? ""}`} aria-hidden="true" />;
}

/** Idle animation used when no audio is being analysed. */
function drift(time: number, levels: Float32Array): void {
  for (let i = 0; i < levels.length; i += 1) {
    const slow = Math.sin(time * (0.5 + (i % 6) * 0.11) + i * 0.5);
    const beat = Math.sin(time * 1.7 + i * 0.21);
    levels[i] = 0.09 + slow * 0.05 + beat * 0.02;
  }
}

function average(values: Float32Array, from: number, to: number): number {
  let sum = 0;
  let count = 0;
  for (let i = from; i < to; i += 1) {
    sum += values[i] ?? 0;
    count += 1;
  }
  return count === 0 ? 0 : sum / count;
}

export interface ModePickerProps {
  mode: VisualizerMode;
  onChange: (mode: VisualizerMode) => void;
  /** Compact styling for tight overlays. */
  compact?: boolean;
}

/** Segmented control for the three visualiser modes. */
export function ModePicker({ mode, onChange, compact }: ModePickerProps): JSX.Element {
  const select = useCallback(
    (next: VisualizerMode) => () => onChange(next),
    [onChange],
  );

  return (
    <div className={`viz-picker${compact ? " viz-picker--compact" : ""}`} role="group" aria-label="Visualiser mode">
      {VISUALIZER_MODES.map((entry) => (
        <button
          key={entry}
          type="button"
          className={`viz-picker__btn${entry === mode ? " is-active" : ""}`}
          aria-pressed={entry === mode}
          onClick={select(entry)}
        >
          {entry}
        </button>
      ))}
    </div>
  );
}
